import { useState } from 'react'
import { fetchDemoDiff } from '../data/api'
import type { DemoDiff as Diff, DemoRef } from '../types'

// What changed between two demos of the same team.
//
// The folder's aggregate averages everything you hold, which is right for "what do they do" and
// exactly wrong for "what did they change" — a team that stopped hitting B appears in it as a team
// that half-does both. This subtracts one demo from another instead.

const ARROW: Record<string, string> = { up: '▲', down: '▼', same: '·' }

/** Direction is about the number, not about whether the change is good — a faster time to site is
 *  "down" and is usually bad news for you. Colour would assert a judgement the data doesn't carry,
 *  so the arrow is neutral and the note says what it means when it isn't obvious. */
const TONE = 'var(--text-dim)'

export function DemoDiff({ opponentId, demos }: { opponentId: string; demos: DemoRef[] }) {
  const [a, setA] = useState('')
  const [b, setB] = useState('')
  const [diff, setDiff] = useState<Diff | null>(null)
  const [err, setErr] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  // Two demos to compare is the whole precondition; below that there is nothing to say.
  if (demos.length < 2) return null

  const run = async (x: string, y: string) => {
    if (!x || !y || x === y) return
    setBusy(true)
    setErr(null)
    const res = await fetchDemoDiff(opponentId, x, y)
    setBusy(false)
    if ('error' in res) {
      setErr(res.error === 'demo_not_folded' ? "One of those demos hasn't been folded yet." : res.error)
      setDiff(null)
      return
    }
    setDiff(res)
  }

  const label = (d: DemoRef) => `${d.map.replace('de_', '')} · ${d.addedAt.slice(0, 10)}`

  return (
    <div className="mt-3">
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <select
          value={a}
          onChange={(e) => {
            setA(e.target.value)
            run(e.target.value, b)
          }}
          aria-label="Earlier demo"
          className="border border-border bg-bg-2 px-2 py-1 text-text outline-none"
          style={{ borderRadius: 'var(--radius)' }}
        >
          <option value="">pick a demo…</option>
          {demos.map((d) => (
            <option key={d.demoId} value={d.demoId}>
              {label(d)}
            </option>
          ))}
        </select>
        <span className="text-text-faint">vs</span>
        <select
          value={b}
          onChange={(e) => {
            setB(e.target.value)
            run(a, e.target.value)
          }}
          aria-label="Later demo"
          className="border border-border bg-bg-2 px-2 py-1 text-text outline-none"
          style={{ borderRadius: 'var(--radius)' }}
        >
          <option value="">pick a demo…</option>
          {demos.map((d) => (
            <option key={d.demoId} value={d.demoId}>
              {label(d)}
            </option>
          ))}
        </select>
        {busy && <span className="text-text-faint">comparing…</span>}
      </div>

      {err && (
        <p role="alert" className="mt-3 text-xs text-loss">
          {err}
        </p>
      )}

      {diff && !diff.sameMap && (
        <p className="mt-3 text-xs text-loss">
          Those are different maps. Site preference and pace aren't comparable across maps — read the
          lineup and buy rows only.
        </p>
      )}

      {diff && diff.changes.length === 0 && (
        <p className="mt-3 text-xs text-text-faint">
          Nothing worth reporting — same five, same shape, same pace. That's a real answer, and the
          useful one before a rematch.
        </p>
      )}

      {diff && diff.changes.length > 0 && (
        <ul className="mt-3 space-y-2">
          {diff.changes.map((c, i) => (
            <li key={`${c.kind}-${i}`} className="border-l-2 border-border pl-3">
              <div className="flex flex-wrap items-baseline gap-2 text-sm">
                <span className="text-text">{c.label}</span>
                <span className="tabular-nums text-text-faint">{c.before}</span>
                <span style={{ color: TONE }}>{ARROW[c.direction]}</span>
                <span className="tabular-nums text-text">{c.after}</span>
              </div>
              {c.note && <p className="mt-0.5 text-[11px] text-text-faint">{c.note}</p>}
            </li>
          ))}
        </ul>
      )}

      <p className="mt-3 text-[11px] leading-relaxed text-text-faint">
        Only differences big enough to act on are listed. Site preference is a share of their T
        rounds and utility is per round, so a longer demo isn't mistaken for a change; pace is the
        median, so one lurk round doesn't drag it.
      </p>
    </div>
  )
}
